import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { Habit } from '../../types/habit';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface HabitFormProps {
  isOpen: boolean;
  onClose: () => void;
  onHabitCreated: () => void;
  initialHabit?: Habit;
  isEditMode?: boolean;
}

const HabitForm: React.FC<HabitFormProps> = ({
  isOpen,
  onClose,
  onHabitCreated,
  initialHabit,
  isEditMode = false
}) => {
  const [formData, setFormData] = useState<Partial<Habit>>({
    name: '',
    description: '',
    frequency: 'daily',
    target_count: 1,
    current_streak: 0,
    longest_streak: 0,
    is_active: true,
    tracking_type: 'boolean',
    good_bad: 'good',
    reminder_time: '',
    reminder_enabled: false,
    category: undefined,
    automation_config: {},
    icon: 'CheckCircleIcon',
    color: '#3B82F6'
  });

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      frequency: 'daily',
      target_count: 1,
      current_streak: 0,
      longest_streak: 0,
      is_active: true,
      tracking_type: 'boolean',
      good_bad: 'good',
      reminder_time: '',
      reminder_enabled: false,
      category: undefined,
      automation_config: {},
      icon: 'CheckCircleIcon',
      color: '#3B82F6'
    });
    setError(null);
  };

  useEffect(() => {
    if (initialHabit) {
      setFormData(initialHabit);
    } else if (isOpen) {
      resetForm();
    }
  }, [initialHabit, isOpen]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) : value
    }));
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === 'automation_config') {
      try {
        const parsed = JSON.parse(value);
        setFormData(prev => ({
          ...prev,
          [name]: parsed
        }));
      } catch {
        // If invalid JSON, store as empty object
        setFormData(prev => ({
          ...prev,
          [name]: {}
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateReminderTime = (time: string): boolean => {
    if (!time) return true; // Empty time is valid (optional field)
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Validate reminder time format
    if (formData.reminder_time && !validateReminderTime(formData.reminder_time)) {
      setError('Reminder time must be in HH:MM format (e.g., 08:00)');
      setLoading(false);
      return;
    }

    try {

      let response;
      if (isEditMode && initialHabit) {
        response = await apiClient.put(`/api/habits/${initialHabit.id}/`, formData);
      } else {
        response = await apiClient.post('/api/habits/', formData);
      }

      if (response.status >= 200 && response.status < 300) {
        onHabitCreated();
        onClose();
      } else {
        throw new Error(`Failed to ${isEditMode ? 'update' : 'create'} habit (${response.status})`);
      }
    } catch (err: unknown) {
      console.error('Error saving habit:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to save habit. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Shared styles
  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: TYPOGRAPHY.size.sm,
    fontWeight: TYPOGRAPHY.weight.medium,
    color: COLORS.text.secondary,
  };

  const inputStyle: React.CSSProperties = {
    marginTop: SPACING[4],
    display: 'block',
    width: '100%',
    borderRadius: RADIUS,
    border: `1px solid ${COLORS.border.default}`,
    padding: `${SPACING[8]} ${SPACING[12]}`,
    fontSize: TYPOGRAPHY.size.sm,
    fontFamily: TYPOGRAPHY.body.fontFamily,
    color: COLORS.text.primary,
    backgroundColor: COLORS.bg.panel,
    outline: 'none',
  };

  const checkboxStyle: React.CSSProperties = {
    height: '16px',
    width: '16px',
    borderRadius: '4px',
    border: `1px solid ${COLORS.border.default}`,
    accentColor: COLORS.accent.default,
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: 50 }} onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          }} />
        </Transition.Child>

        <div style={{ position: 'fixed', inset: 0, overflowY: 'auto' }}>
          <div style={{
            display: 'flex',
            minHeight: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING[16],
            textAlign: 'center',
          }}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel style={{
                width: '100%',
                maxWidth: '448px',
                transform: 'none',
                overflow: 'hidden',
                borderRadius: '16px',
                backgroundColor: COLORS.bg.panel,
                padding: SPACING[24],
                textAlign: 'left',
                verticalAlign: 'middle',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                transition: 'all',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: SPACING[16],
                }}>
                  <Dialog.Title style={{
                    fontSize: TYPOGRAPHY.size.lg,
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.primary,
                  }}>
                    {isEditMode ? 'Edit Habit' : 'Create New Habit'}
                  </Dialog.Title>
                  <button
                    onClick={handleClose}
                    style={{
                      color: COLORS.text.tertiary,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <XMarkIcon style={{ width: '24px', height: '24px' }} />
                  </button>
                </div>

                {error && (
                  <div style={{
                    marginBottom: SPACING[16],
                    padding: SPACING[12],
                    fontSize: TYPOGRAPHY.size.sm,
                    color: COLORS.status.error,
                    backgroundColor: '#FEF2F2',
                    borderRadius: RADIUS,
                    border: `1px solid #FECACA`,
                  }}>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                  <div>
                    <label htmlFor="name" style={labelStyle}>
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label htmlFor="description" style={labelStyle}>
                      Description (optional)
                    </label>
                    <input
                      type="text"
                      id="description"
                      name="description"
                      value={formData.description || ''}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label htmlFor="icon" style={labelStyle}>
                      Icon *
                    </label>
                    <select
                      id="icon"
                      name="icon"
                      required
                      value={formData.icon || 'CheckCircleIcon'}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="CheckCircleIcon">Check Circle</option>
                      <option value="FireIcon">Fire</option>
                      <option value="HeartIcon">Heart</option>
                      <option value="BookOpenIcon">Book</option>
                      <option value="AcademicCapIcon">Academic</option>
                      <option value="BeakerIcon">Science</option>
                      <option value="CakeIcon">Food</option>
                      <option value="CameraIcon">Camera</option>
                      <option value="ChatBubbleLeftIcon">Chat</option>
                      <option value="CodeBracketIcon">Code</option>
                      <option value="CogIcon">Settings</option>
                      <option value="CpuChipIcon">Technology</option>
                      <option value="CreditCardIcon">Finance</option>
                      <option value="CurrencyDollarIcon">Money</option>
                      <option value="DocumentTextIcon">Document</option>
                      <option value="FilmIcon">Film</option>
                      <option value="GlobeAltIcon">Globe</option>
                      <option value="MusicalNoteIcon">Music</option>
                      <option value="PaintBrushIcon">Art</option>
                      <option value="PuzzlePieceIcon">Puzzle</option>
                      <option value="RocketLaunchIcon">Rocket</option>
                      <option value="ShieldCheckIcon">Security</option>
                      <option value="SunIcon">Sun</option>
                      <option value="TrophyIcon">Trophy</option>
                      <option value="WrenchScrewdriverIcon">Tools</option>
                      <option value="Squares2X2Icon">Grid</option>
                      <option value="ClockIcon">Clock</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="color" style={labelStyle}>
                      Color *
                    </label>
                    <select
                      id="color"
                      name="color"
                      required
                      value={formData.color || '#3B82F6'}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="#3B82F6">Blue (Primary)</option>
                      <option value="#10B981">Green</option>
                      <option value="#F59E0B">Amber</option>
                      <option value="#EF4444">Red</option>
                      <option value="#8B5CF6">Purple</option>
                      <option value="#EC4899">Pink</option>
                      <option value="#6B7280">Gray</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="frequency" style={labelStyle}>
                      Frequency *
                    </label>
                    <select
                      id="frequency"
                      name="frequency"
                      required
                      value={formData.frequency}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="target_count" style={labelStyle}>
                      Target Count *
                    </label>
                    <input
                      type="number"
                      id="target_count"
                      name="target_count"
                      required
                      value={formData.target_count}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label htmlFor="current_streak" style={labelStyle}>
                      Current Streak
                    </label>
                    <input
                      type="number"
                      id="current_streak"
                      name="current_streak"
                      value={formData.current_streak}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label htmlFor="longest_streak" style={labelStyle}>
                      Longest Streak
                    </label>
                    <input
                      type="number"
                      id="longest_streak"
                      name="longest_streak"
                      value={formData.longest_streak}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      id="is_active"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      style={checkboxStyle}
                    />
                    <label htmlFor="is_active" style={{
                      marginLeft: SPACING[8],
                      display: 'block',
                      fontSize: TYPOGRAPHY.size.sm,
                      color: COLORS.text.secondary,
                    }}>
                      Active
                    </label>
                  </div>

                  <div>
                    <label htmlFor="tracking_type" style={labelStyle}>
                      Tracking Type *
                    </label>
                    <select
                      id="tracking_type"
                      name="tracking_type"
                      required
                      value={formData.tracking_type}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="boolean">Boolean (Yes/No)</option>
                      <option value="count">Count (Number of times)</option>
                      <option value="duration">Duration (Time spent)</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="good_bad" style={labelStyle}>
                      Good/Bad *
                    </label>
                    <select
                      id="good_bad"
                      name="good_bad"
                      required
                      value={formData.good_bad}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="good">Good</option>
                      <option value="bad">Bad</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="reminder_time" style={labelStyle}>
                      Reminder Time (optional, HH:MM format)
                    </label>
                    <input
                      type="text"
                      id="reminder_time"
                      name="reminder_time"
                      placeholder="08:00"
                      value={formData.reminder_time || ''}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label htmlFor="reminder_enabled" style={labelStyle}>
                      Reminder Enabled
                    </label>
                    <input
                      type="checkbox"
                      id="reminder_enabled"
                      name="reminder_enabled"
                      checked={formData.reminder_enabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, reminder_enabled: e.target.checked }))}
                      style={checkboxStyle}
                    />
                  </div>

                  <div>
                    <label htmlFor="category" style={labelStyle}>
                      Category ID (optional)
                    </label>
                    <input
                      type="number"
                      id="category"
                      name="category"
                      value={formData.category || ''}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label htmlFor="automation_config" style={labelStyle}>
                      Automation Config (optional, JSON format)
                    </label>
                    <textarea
                      id="automation_config"
                      name="automation_config"
                      value={JSON.stringify(formData.automation_config, null, 2) || '{}'}
                      onChange={handleTextareaChange}
                      rows={3}
                      style={{
                        ...inputStyle,
                        resize: 'vertical',
                      }}
                      placeholder='{"key": "value"}'
                    />
                  </div>

                  <div style={{
                    marginTop: SPACING[24],
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: SPACING[12],
                  }}>
                    <button
                      type="button"
                      onClick={handleClose}
                      style={{
                        padding: `${SPACING[8]} ${SPACING[16]}`,
                        fontSize: TYPOGRAPHY.size.sm,
                        fontWeight: TYPOGRAPHY.weight.medium,
                        color: COLORS.text.secondary,
                        backgroundColor: COLORS.bg.panel,
                        border: `1px solid ${COLORS.border.default}`,
                        borderRadius: RADIUS,
                        cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        padding: `${SPACING[8]} ${SPACING[16]}`,
                        fontSize: TYPOGRAPHY.size.sm,
                        fontWeight: TYPOGRAPHY.weight.medium,
                        color: COLORS.bg.panel,
                        backgroundColor: COLORS.accent.default,
                        border: 'none',
                        borderRadius: RADIUS,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.5 : 1,
                      }}
                    >
                      {loading ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default HabitForm;
